import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserProfileDialog from '../User/UserProfileDialog';

interface UserProfileDrawerProps {
  open: boolean;
  onClose: () => void;
  onOpenProfileDialog?: () => void;
}

const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({ open, onClose, onOpenProfileDialog }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };
  
  const handleOpenProfileDialog = () => {
    if (onOpenProfileDialog) {
      onOpenProfileDialog();
    }
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (!user) return '?';
    return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  if (!user) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(12px)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
        }
      }}
    >
      <Box sx={{ width: 300, p: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'white'
        }}>
          <Typography variant="h6">Profile</Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          p: 3,
          backgroundColor: 'rgba(59, 130, 246, 0.05)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'white'
        }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: 'primary.dark',
              mb: 2,
              fontSize: '2rem'
            }}
          >
            {getInitials()}
          </Avatar>
          <Typography variant="h6">
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {user.email}
          </Typography>
          <Typography variant="caption" sx={{ mt: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
            {user.role}
          </Typography>
        </Box>
        
        <List sx={{ color: 'white' }}>
          <ListItem 
            button 
            onClick={handleOpenProfileDialog}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(8px)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="My Profile" />
          </ListItem>
          
          <ListItem 
            button 
            onClick={() => handleNavigation('/change-password')}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(8px)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              <SecurityIcon />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
          </ListItem>
          
          {user.role === 'administrator' && (
            <>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <ListItem 
                button 
                onClick={() => handleNavigation('/utilities/admin/lookups')}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(8px)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText primary="Admin Panel" />
              </ListItem>
            </>
          )}
          
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <ListItem 
            button 
            onClick={handleLogout}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                backdropFilter: 'blur(8px)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(239, 68, 68, 0.8)' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              sx={{ color: 'rgba(239, 68, 68, 0.9)' }}
            />
          </ListItem>
        </List>
        
        <Box sx={{ p: 2, mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Utilities CRM v1.0.0
          </Typography>
        </Box>
      </Box>
      
    </Drawer>
  );
};

export default UserProfileDrawer;
